// Module-level cache so elements that connect after the event was fired (e.g. quickview)
// can still replay the last known delivery status.
let _lastDeliveryStatusDetail = null;

// Units available per `<variantId>|<zip>` at the store that postcode routes to (or its first stocked
// fallback), as variant-location-stock.js last heard from the app. Module-level for the same reason as
// the status cache above: a quickview can connect after the figure arrived.
const _routedStock = new Map();

// Whether the app offers local delivery to a postcode, per `<zip>`: the postcode list checkout enforces.
// When known it decides delivery on this panel instead of the distance from the address to each store,
// which disagreed with checkout for postcodes that reach past a store's radius (FPS-807).
const _routedDeliverable = new Map();

// Drawer previews the buyer walked away from. The drawer keeps its pending address after Cancel and
// auto-confirms on any "available" broadcast, so a verdict that lands after Cancel, even once the drawer is
// reopened, must not re-render that preview. Marked on close, by object: a fresh submission geocodes a new
// result, so only the abandoned one is shut out. A confirm clears the pending address before it closes.
const _abandonedPreviews = new WeakSet();
document.addEventListener('toggle', (event) => {
  const drawer = event.target;
  const pending = drawer?.pendingGeocodeResult;
  if (drawer?.localName === 'address-drawer' && !drawer.open && pending && typeof pending === 'object') {
    _abandonedPreviews.add(pending);
  }
});

// The theme's own en.default.json wording. A store's theme content can override a string to blank, and a
// blank status leaves the previous text on screen, so the in-stock line survives an out-of-stock answer.
const DEFAULT_TEXTS = {
  locationInStock: 'Delivery available in your area and Product in Stock',
  locationOutOfStock: 'Delivery available in your area but Product Out of Stock',
  locationNotAvailable: 'Delivery not available in your area',
};

// Same two shapes deliveryZip() in variant-location-stock.js reads, the parsed postcode or one recovered
// from the formatted address. The two must agree, or no routed figure ever matches this address.
function postcodeOf(address) {
  const parsed = address?.address_components?.postal_code;
  if (typeof parsed === 'string' && parsed.trim() !== '') return parsed.trim();

  const formatted = typeof address?.formatted_address === 'string' ? address.formatted_address : '';
  const fallback = formatted.match(/\b[A-Z]{2}\s+(\d{5})(?:-\d{4})?\b/);
  return fallback ? fallback[1] : null;
}

// FPS-679 — a store can have Shopify's Local delivery toggle off while still stocking the
// product and covering the address. Shopify exposes local pickup per location to the
// storefront but never local delivery, so the location metaobject's
// supported_delivery_methods (e.g. ["Local Delivery","BOPIS (Pickup)"]) is the only
// representation the theme can read. Blank means the store fulfils nothing, same as
// sections/locations.liquid treats it.
function supportsLocalDelivery(location) {
  const methods = location?.deliveryMethods;
  if (!Array.isArray(methods)) return false;
  return methods.some((method) => String(method).toLowerCase().includes('delivery'));
}

if (!customElements.get('location-delivery-status')) {
  customElements.define(
    'location-delivery-status',
    class LocationDeliveryStatus extends HTMLElement {
      constructor() {
        super();
        this.refCache = {};
        this._onDeliveryAddressUpdated = this.onDeliveryAddressUpdated.bind(this);
        this._onDeliveryAddressStatusUpdated = this.onDeliveryAddressStatusUpdated.bind(this);
        this._onRoutedStock = this.onRoutedStock.bind(this);
      }

      refs(value) {
        if (this.refCache[value]) return this.refCache[value];
        this.refCache[value] = this.querySelector(`[ref='${value}']`);
        return this.refCache[value];
      }


      connectedCallback() {
        if (this.dataset.type === 'status') {
          document.addEventListener('deliveryAddress:statusUpdated', this._onDeliveryAddressStatusUpdated);
          document.addEventListener('deliveryAddress:statusDisplayed', this._onDeliveryAddressStatusUpdated);
          // Replay last status in case this element connected after the event was dispatched (e.g. quickview).
          if (_lastDeliveryStatusDetail) {
            this.onDeliveryAddressStatusUpdated({ detail: _lastDeliveryStatusDetail });
          }
        } else {
          document.addEventListener('deliveryAddress:updated', this._onDeliveryAddressUpdated);
          document.addEventListener('deliveryAddress:routedStock', this._onRoutedStock);
          // Render immediately from cookie in case the event already fired before this element connected.
          this.render();
        }
      }

      disconnectedCallback() {
        if (this.dataset.type === 'status') {
          document.removeEventListener('deliveryAddress:statusUpdated', this._onDeliveryAddressStatusUpdated);
          document.removeEventListener('deliveryAddress:statusDisplayed', this._onDeliveryAddressStatusUpdated);
        }
        else {
          document.removeEventListener('deliveryAddress:updated', this._onDeliveryAddressUpdated);
          document.removeEventListener('deliveryAddress:routedStock', this._onRoutedStock);
        }
      }

      onDeliveryAddressStatusUpdated(event) {
        const { msg, className, available } = event.detail;
        const isOutOfStock = available === false;
        const context = this.renderByRef('addressStatus', msg);

        if (context) {
          context.classList.remove('invalid', 'valid');
          className && context.classList.add(className);
          context.classList.toggle('out-of-stock', isOutOfStock);
        }
      }

      onRoutedStock(event) {
        const { zip, figures, deliverable } = event.detail ?? {};
        if (!zip || !figures) return;

        // A changed delivery verdict changes what every listener of statusUpdated should hear (the drawer's
        // Confirm, each Add to cart), unlike a stock figure, so that render broadcasts. An answer without the
        // flag (a failed or timed-out call) drops the old verdict and puts the radius check back in charge.
        const known = typeof deliverable === 'boolean' ? deliverable : undefined;
        const verdictChanged = _routedDeliverable.get(zip) !== known;
        if (known === undefined) _routedDeliverable.delete(zip);
        else _routedDeliverable.set(zip, known);

        for (const [variantId, available] of Object.entries(figures)) {
          const key = `${variantId}|${zip}`;
          if (typeof available === 'number') _routedStock.set(key, available);
          else _routedStock.delete(key);
        }

        // An answer about another product (a quick view over this PDP) leaves this panel alone. Rendered
        // without a broadcast: the figure changes only what this panel says, and every listener of
        // statusUpdated already heard this address's answer when it was set.
        const config = this.config ?? this.getConfig();
        // Only while this panel shows the saved address, which is the one the gate asked about. While the
        // drawer previews another (even in the same postcode) the figure is stored, not drawn, and only a
        // changed verdict for the previewed postcode re-renders the preview (below).
        if (config && String(config.id) in figures && this.isShowingSavedAddress()) {
          this.render(null, { broadcast: verdictChanged });
          return;
        }

        // The one exception: the verdict for the postcode the drawer is previewing, which
        // variant-location-stock.js asks for before the address is saved. The preview's first render could
        // only use the radius, so a served postcode past every store's radius would leave Confirm disabled
        // until this re-render broadcasts the app's answer. Only while an open drawer still holds that address
        // as its pending one, and only if the buyer has not closed the drawer on it since (_abandonedPreviews): a
        // late answer must not save an address the buyer walked away from.
        const shown = this._renderedAddress;
        if (
          verdictChanged &&
          shown &&
          !this.isShowingSavedAddress() &&
          postcodeOf(shown) === zip &&
          this.isDrawerPreviewing(shown)
        ) {
          this.render(shown);
        }
      }

      isDrawerPreviewing(address) {
        return [...document.querySelectorAll('address-drawer')].some(
          (drawer) =>
            drawer.open &&
            drawer.pendingGeocodeResult &&
            !_abandonedPreviews.has(drawer.pendingGeocodeResult) &&
            drawer.pendingGeocodeResult.formatted_address === address.formatted_address
        );
      }

      isShowingSavedAddress() {
        const shown = this._renderedAddress;
        const saved = this.getAddressResultFromCookie();
        if (!shown || !saved) return false;

        return (
          shown.formatted_address === saved.formatted_address &&
          Number(shown.latitude) === Number(saved.latitude) &&
          Number(shown.longitude) === Number(saved.longitude)
        );
      }

      onDeliveryAddressUpdated(event) {
        const data = event.detail;
        this.render(data);
      }

      render(data = null, { broadcast = true } = {}) {
        const selected = data ?? this.getAddressResultFromCookie();
        this._renderedAddress = selected;

        const config = this.config ?? this.getConfig();
        this.config = config;

        if (!selected?.latitude || !selected?.longitude || !this.isConnected || !config) {
          this.reset();
          return;
        }

        const { availableLocations = [] } = config;
        this.renderByRef('addressSelected', selected.formatted_address);
        let locationsWithDistance = [];

        const deliveryLocations = availableLocations.filter(supportsLocalDelivery);

        if (deliveryLocations.length > 0) {
          // Calculate distance for each location and add to the object
          locationsWithDistance = deliveryLocations.map((location, index) => {
            const { latlng, deliveryRadius = 0 } = location;
            const lat = Number(latlng?.latitude || 0);
            const lng = Number(latlng?.longitude || 0);

            if (!lat || !lng) {
              console.warn('[LocationDeliveryStatus] Location missing coordinates:', location);
              return {
                ...location,
                distance: Infinity, // Place locations without coords at the end
              };
            }

            const distance = this.calculateDistance(
              selected.latitude,
              selected.longitude,
              lat,
              lng,
              'miles'
            );

            return {
              ...location,
              distance: distance !== null && distance <= Number(deliveryRadius) ? distance : Infinity,
            };
          });
        }
        // Sort locations by distance (nearest first)
        const sortedLocations = locationsWithDistance.filter(location => location.distance !== Infinity).sort((a, b) => a.distance - b.distance);
        const availbleLocation = sortedLocations.filter(location => location.available);
        const nearest = availbleLocation.length ? availbleLocation[0] : sortedLocations[0];

        // A store in the delivery radius stocking the variant is not the store the order goes to: routing
        // sends it to the store that owns this postcode, then that store's fallbacks. When the app says
        // none of those has a unit, Add to cart is already gated (variant-location-stock.js), and this
        // line must say out of stock rather than contradict it. Only ever downgrades: an absent figure
        // means the app had nothing to say, and Liquid's answer stands.
        const zip = postcodeOf(selected);
        const served = zip === null ? undefined : _routedDeliverable.get(zip);
        const routedFigure = zip === null ? undefined : _routedStock.get(`${config.id}|${zip}`);
        // The app's answer wins when it has one: the store is chosen by postcode, so the nearest store by
        // distance says nothing about delivery here. A served postcode is deliverable whatever the radius
        // stores hold, and its stock is the routed store's figure, applied through routedOut so the
        // broadcast below still says "deliverable" and a stock-out never reads as an undeliverable address.
        // With no figure (the app had nothing to say about stock), Liquid's stock answer stands: a served
        // postcode alone is no evidence the product is in stock (FPS-803).
        let shown = nearest;
        let stockOut = false;
        if (served === false) shown = null;
        else if (served === true) {
          shown = { ...nearest, available: true };
          if (routedFigure == null) stockOut = !nearest?.available;
        }
        const routedOut = Boolean(shown?.available) && (routedFigure === 0 || stockOut);

        this.renderAddressStatus(shown, { routedOut, broadcast });
      }

      renderAddressStatus(data = null, { routedOut = false, broadcast = true } = {}) {

        let status = '', className = '';
        const inStock = Boolean(data?.available) && !routedOut;

        if (data) {
          status = this.text(inStock ? 'locationInStock' : 'locationOutOfStock');
          className = inStock ? '' : 'invalid';
        }
        else {
          status = this.text('locationNotAvailable');
          className = 'invalid';
        }

        // "No serving store at all" has to carry the .out-of-stock marker too, not just
        // "store found but out of stock". DeliveryOptions.dispatchSubmitButtonUpdate reads that
        // marker on its own — without it, re-picking the Delivery radio re-enables Add to Cart
        // even though this element just said delivery is unavailable (FPS-679).
        const isOutOfStock = !inStock;
        const context = this.renderByRef('addressStatus', status);

        if (context) {
          context.classList.remove('invalid', 'valid');
          className && context.classList.add(className);
          context.classList.toggle('out-of-stock', isOutOfStock);
        }
        // `available` stays Liquid's answer even when the routed figure downgrades the text. Listeners
        // read it as "this address can be delivered to" (AddressDrawer's Confirm, every ProductInfo's
        // button), and a stock figure for one product must not block confirming an address or another
        // product's Add to cart. The gate in variant-location-stock.js already holds this product's.
        if (broadcast) {
          const statusDetail = { msg: status, className, available: data?.available ?? false };
          document.dispatchEvent(new CustomEvent('deliveryAddress:statusUpdated', { detail: statusDetail }));
        }

        // The data-type="status" mirrors (the address drawer) show what this panel shows, routed figure
        // included, through an event only they listen to. Sent last, so it is what they end on.
        const displayed = { msg: status, className, available: inStock };
        _lastDeliveryStatusDetail = displayed;
        document.dispatchEvent(new CustomEvent('deliveryAddress:statusDisplayed', { detail: displayed }));
      }

      text(key) {
        const configured = this.config?.texts?.[key];
        return typeof configured === 'string' && configured.trim() !== '' ? configured : DEFAULT_TEXTS[key];
      }

      renderByRef(ref, value = null) {
        const context = this.refs(ref);
        if (!context || !value) return null;

        // Values here are plain-text status strings (config.texts.*) and the
        // selected formatted address — none carry intentional markup. Use
        // textContent so merchant-configured text can't inject executable HTML.
        context.textContent = value;
        return context;
      }

      reset() {
        console.log('[addressStatus] reset → clearing addressStatus');
        _lastDeliveryStatusDetail = null;
        this.renderByRef('addressSelected', '');
        this.renderByRef('addressStatus', '');
        this.refs('addressStatus')?.classList.remove('out-of-stock', 'invalid', 'valid');

        // Clear locations list if exists
        const container = this.refs('locationsList');
        if (container) {
          container.innerHTML = '';
        }
      }

      /**
       * Get locations sorted by distance without rendering
       * Useful for external components that need sorted data
       * @param {Object} userAddress - Address object with latitude/longitude
       * @returns {Array} Sorted locations with distance data
       */
      getSortedLocations(userAddress = null) {
        const selected = userAddress ?? this.getAddressResultFromCookie();
        const config = this.getConfig();

        if (!selected?.latitude || !selected?.longitude || !config?.availableLocations) {
          return [];
        }

        const { availableLocations = [] } = config;

        // Calculate distance for each location
        const locationsWithDistance = availableLocations.filter(supportsLocalDelivery).map(location => {
          // Locations come from the same config blob render() reads, where coordinates are
          // nested under latlng. The flat forms are kept for any caller passing its own shape.
          const lat = Number(location.latlng?.latitude || location.latitude || location.lat || 0);
          const lng = Number(location.latlng?.longitude || location.longitude || location.lng || 0);

          if (!lat || !lng) {
            return {
              ...location,
              distance: Infinity,
              distanceText: 'N/A'
            };
          }

          const distance = this.calculateDistance(
            selected.latitude,
            selected.longitude,
            lat,
            lng,
            'miles'
          );

          return {
            ...location,
            distance: distance !== null ? distance : Infinity,
            distanceText: distance !== null ? `${distance.toFixed(1)} mi` : 'N/A'
          };
        });

        // Sort by distance (nearest first)
        return locationsWithDistance.sort((a, b) => a.distance - b.distance);
      }

      /**
       * Get nearest location
       * @param {Object} userAddress - Address object with latitude/longitude
       * @returns {Object|null} Nearest location with distance data
       */
      getNearestLocation(userAddress = null) {
        const sorted = this.getSortedLocations(userAddress);
        return sorted.length > 0 ? sorted[0] : null;
      }

      // FPS-677: after an AJAX variant swap, product-info.js replaces this element's
      // config <script> innerHTML with the new variant's data, but render() below caches
      // `this.config` on first read -- drop the cache so the swapped-in data is re-parsed.
      refreshNow() {
        this.config = null;
        this.render();
      }

      getConfig() {
        const script = this.querySelector('script[type="application/json"]');
        if (!script) return null;

        const json = script.textContent?.trim();
        if (!json) return null;

        try {
          return JSON.parse(json);
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      getAddressResultFromCookie() {
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        const prefix = 'addressDrawerResult=';
        const cookie = cookies.find((entry) => entry.startsWith(prefix));
        if (!cookie) return null;

        const value = cookie.substring(prefix.length);
        if (!value) return null;

        try {
          return JSON.parse(decodeURIComponent(value));
        } catch (error) {
          console.error('[LocationDeliveryStatus] Error parsing result cookie:', error);
          return null;
        }
      }

      /**
       * Calculate distance between two coordinates using Haversine formula
       * This is a client-side calculation, no API calls needed
       * @param {number} lat1 - Latitude of first point
       * @param {number} lng1 - Longitude of first point
       * @param {number} lat2 - Latitude of second point
       * @param {number} lng2 - Longitude of second point
       * @param {string} unit - Unit of measurement: 'km' (default), 'miles', 'meters'
       * @returns {number} Distance in specified unit
       */
      calculateDistance(lat1, lng1, lat2, lng2, unit = 'km') {
        // Validate inputs
        const coords = [lat1, lng1, lat2, lng2];
        if (coords.some(coord => typeof coord !== 'number' || isNaN(coord))) {
          console.error('[LocationDeliveryStatus] Invalid coordinates provided');
          return null;
        }

        // Haversine formula
        const R = 6371; // Earth's radius in kilometers
        const dLat = this._toRadians(lat2 - lat1);
        const dLng = this._toRadians(lng2 - lng1);

        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(this._toRadians(lat1)) *
          Math.cos(this._toRadians(lat2)) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distanceKm = R * c;

        // Convert to requested unit
        switch (unit) {
          case 'miles':
            return distanceKm * 0.621371;
          case 'meters':
            return distanceKm * 1000;
          case 'km':
          default:
            return distanceKm;
        }
      }

      /**
       * Helper function to convert degrees to radians
       * @param {number} degrees
       * @returns {number} Radians
       */
      _toRadians(degrees) {
        return degrees * (Math.PI / 180);
      }

      /**
       * Calculate distance using Google Maps Distance Matrix Service (SDK)
       * This provides more accurate results considering actual routes
       * @param {Object} origin - {lat: number, lng: number}
       * @param {Object} destination - {lat: number, lng: number}
       * @param {string} apiKey - Google Maps API key
       * @param {Object} options - Optional parameters (mode, avoid, units, etc.)
       * @returns {Promise<Object>} Distance and duration information
       */
      async calculateDistanceWithAPI(origin, destination, apiKey, options = {}) {
        if (!origin?.lat || !origin?.lng || !destination?.lat || !destination?.lng) {
          console.error('[LocationDeliveryStatus] Invalid origin or destination coordinates');
          return null;
        }

        if (!apiKey) {
          console.error('[LocationDeliveryStatus] Google Maps API key is required');
          return null;
        }

        try {
          const result = await window.GoogleMapsGeocoder.getDistanceMatrix(origin, destination, apiKey, options);
          return result;
        } catch (error) {
          console.error('[LocationDeliveryStatus] Error calculating distance:', error);
          return null;
        }
      }

      /**
       * Check if a location is within delivery radius
       * @param {Object} userLocation - {latitude: number, longitude: number}
       * @param {Object} storeLocation - {latitude: number, longitude: number}
       * @param {number} radiusMi - Maximum delivery radius in miles
       * @returns {boolean} True if within radius
       */
      isWithinDeliveryRadius(userLocation, storeLocation, radiusMi) {
        const distance = this.calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          storeLocation.latitude,
          storeLocation.longitude,
          'miles'
        );

        return distance !== null && distance <= radiusMi;
      }
    }
  );
}